import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Activity } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { CoinIcon, Pct, Spark, fmtPrice } from "@/components/layernav/primitives";
import { COLORS } from "@/components/home/LayerIndexCard";
import { AuroraGauge } from "@/components/home/AuroraArc";
import { sectorTrack } from "./SectorHero";

const GLASS = "rounded-xl border border-white/[0.09] bg-black/55 backdrop-blur-md";
const SNAP = ["BTC", "ETH", "SOL", "XRP"];
const ROW = "grid grid-cols-[minmax(0,1fr)_86px_58px_58px] items-center gap-x-2";

const SnapRow = ({ c }: { c: any }) => {
  const { data: detail } = useQuery({
    queryKey: ["nav-spark", c.symbol],
    queryFn: async () => (await api.get(`/markets/${c.symbol}?range=7d`)).data,
    staleTime: 5 * 60 * 1000,
  });
  const chart: number[] = (detail?.chart || []).map((p: any) => p.price);
  const up7 = chart.length > 1 ? chart[chart.length - 1] >= chart[0] : c.change_24h >= 0;
  return (
    <Link to={`/coin/${c.symbol}`} data-testid={`sector-snapshot-${c.symbol}`}
      onClick={() => sectorTrack("market_module_interaction", c.symbol)}
      className={`${ROW} rounded-lg px-2 py-2 -mx-2 hover:bg-white/[0.05] transition-colors`}>
      <span className="flex items-center gap-2.5 min-w-0">
        <CoinIcon symbol={c.symbol} size={22} />
        <span className="font-mono text-[12.5px] font-bold text-gray-200">{c.symbol}</span>
      </span>
      <span className="font-mono text-[13px] text-white text-right">{fmtPrice(c.price)}</span>
      <Pct v={c.change_24h} className="!text-[11px] text-right" />
      <span className="flex justify-end">
        <Spark points={chart} up={up7} className="w-13 h-4 w-[52px]" />
        <span className="sr-only">7 day trend {up7 ? "up" : "down"}</span>
      </span>
    </Link>
  );
};

export const SectorMarketSnapshot = ({ testid = "sector-market-snapshot" }: { testid?: string }) => {
  const { data } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    refetchInterval: 90000,
  });
  const coins = (data?.coins || []).filter((c: any) => SNAP.includes(c.symbol));
  return (
    <div data-testid={testid} className={`${GLASS} p-4`}>
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#A78BFA]">Market Snapshot</p>
      {coins.length > 0 ? (
        <div className="mt-2.5">
          <div className={`${ROW} font-mono text-[9px] uppercase tracking-wider text-gray-500 mb-1`}>
            <span>Asset</span><span className="text-right">Price</span>
            <span className="text-right">24H %</span><span className="text-right">7D</span>
          </div>
          {coins.map((c: any) => <SnapRow key={c.symbol} c={c} />)}
        </div>
      ) : (
        <p className="font-mono text-[11px] text-gray-600 py-4" data-testid="sector-snapshot-unavailable">
          Market data temporarily unavailable
        </p>
      )}
      <Link to="/markets" data-testid="sector-view-all-prices"
        onClick={() => sectorTrack("market_module_interaction", "view_all_prices")}
        className="group mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-white/[0.12] bg-white/[0.04] py-2 text-[12px] font-bold text-gray-200 hover:border-[#00E5FF]/50 hover:text-white transition-colors">
        View all prices
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
};

const SPECTRUM_BG =
  "linear-gradient(90deg,#C81E1E 0%,#F0564E 18%,#F97316 34%,#8B5CF6 50%,#34D399 64%,#06D6F5 80%,#3B82F6 100%)";

const useCountUp = (target: number | null) => {
  const [n, setN] = useState<number | null>(null);
  const ran = useRef(false);
  useEffect(() => {
    if (target == null || ran.current) return;
    ran.current = true;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setN(target); return; }
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const p = Math.min((t - t0) / 900, 1);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return n;
};

export const SectorLayerIndex = ({ testid = "sector-layer-index", sector = "crypto" }: { testid?: string; sector?: string }) => {
  const { data, isError } = useQuery({
    queryKey: ["layer-index"],
    queryFn: async () => (await api.get("/layer-index")).data,
    staleTime: 5 * 60 * 1000,
  });
  const value: number | null = data?.value ?? null;
  const history: { value: number }[] = data?.history || [];
  const yesterday = history.length > 1 ? history[history.length - 2].value : null;
  const delta = value != null && yesterday != null ? value - yesterday : null;
  const color = data ? COLORS[data.label] || "#A3A3A3" : "#A3A3A3";
  const drivers: { label: string; dir: string }[] = (data?.drivers || []).slice(0, 3);
  const shown = useCountUp(value);
  const done = shown != null && shown === value;
  return (
    <div data-testid={testid} className={`${GLASS} p-4`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#A78BFA] flex items-center gap-1.5">
          <Activity aria-hidden className="h-3 w-3 text-[#8B5CF6]" />
          Layer Index
        </p>
        {data?.computed_at && (
          <span className="font-mono text-[8px] uppercase tracking-widest text-gray-600">
            Upd {timeAgo(data.computed_at).replace(/ minutes?/, "m").replace(/ hours?/, "h").replace(" ago", "").replace("just now", "now")}
          </span>
        )}
      </div>
      {isError ? (
        <p className="font-mono text-[11px] text-gray-600 py-4" data-testid="sector-layer-index-error">
          Layer Index temporarily unavailable
        </p>
      ) : (
        <div className="flex items-center gap-4 mt-1.5">
          <div className="w-[148px] shrink-0 group" data-testid="sector-layer-index-ring">
            <AuroraGauge pct={shown ?? 0} num={shown ?? "—"} label={data?.label} labelColor={color} delta={delta}
              done={done} animating={value != null && !done} glint={false} dormant={value == null} compact />
          </div>
          <div className="min-w-0 flex-1">
            {value != null && (
              <p className="sr-only" data-testid="sector-layer-index-value">
                Layer Index {value} out of 100 — {data?.label}{delta != null ? `, ${delta > 0 ? "up" : delta < 0 ? "down" : "flat"} ${Math.abs(delta)} points in 24 hours` : ""}.
              </p>
            )}
            {drivers.length > 0 && (
              <ul className="space-y-1.5" data-testid="sector-layer-index-drivers">
                {drivers.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11.5px] text-gray-300 leading-snug">
                    <span aria-hidden className={`font-mono ${d.dir === "up" ? "text-[#34D399]" : "text-[#EF4444]"}`}>
                      {d.dir === "up" ? "↑" : "↓"}
                    </span>
                    {d.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <Link to="/layer-index" data-testid="sector-explore-layer-index"
        onClick={() => sectorTrack("market_module_interaction", "explore_layer_index", sector)}
        className="group mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#7C3AED]/50 bg-[#7C3AED]/[0.08] py-2 text-[12px] font-bold text-white hover:border-[#7C3AED] hover:bg-[#7C3AED]/[0.16] transition-colors">
        Explore Layer Index
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
};
